import { useMemo, useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Pause, Play, OctagonX } from 'lucide-react'
import { setRolloutStatus, pauseAll, resumeAll, killAll, type RolloutStatus } from '@/api/priceRollout'
import { ApiError } from '@/api/client'
import { Panel } from '@/components/Panel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ArtikelKennung, NaechsterSchritt, VersandChip, WirkungChip } from '@/components/rollout/RolloutAnzeige'
import { BestaetigungsDialog } from '@/components/rollout/BestaetigungsDialog'
import { RolloutVerlauf, useRolloutVerlauf } from '@/components/rollout/RolloutVerlauf'
import { fmtEUR } from '@/lib/pricing-format'
import { cn } from '@/lib/utils'
import { istQuelleText, kennungSuchtext, kontoKurz, portalText, schreibzielText, wirkungText } from '@/lib/rollout-klartext'
import { paarZustand, rolloutGrundText } from '@/pages/grundartikel/tabs/rollout/rolloutView'
import { gruppiereRollouts, type RolloutZeile } from './rolloutPaare'
import { useRolloutStatus, useRolloutStatusNeuLaden } from './useRolloutStatus'

const STATUS_STYLE: Record<RolloutStatus['status'], string> = {
  active: 'bg-success/20 text-success',
  paused: 'bg-warn/20 text-warn',
  done: 'bg-panel-2 text-muted',
  error: 'bg-coral/20 text-coral',
}
const STATUS_LABEL: Record<RolloutStatus['status'], string> = {
  active: 'aktiv', paused: 'pausiert', done: 'abgeschlossen', error: 'Fehler',
}

const STATUS_TEXT = (r: RolloutStatus) => STATUS_LABEL[r.status] ?? r.status
const fehlerText = (e: unknown) => (e instanceof ApiError ? e.message : 'Aktion fehlgeschlagen')

const istHinweis = (msg: string | null) => !!msg && msg.startsWith('ebay_folgt_basis') && !msg.includes('Basis-Rollout starten')

function StatusZelle({ r, praefix }: { r: RolloutStatus; praefix?: string }) {
  const grund = rolloutGrundText(r.error_message)
  return (
    <div>
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_STYLE[r.status]}`}>
        {praefix ? `${praefix} ` : ''}{STATUS_TEXT(r)}
      </span>
      {grund && (
        // eBay folgt Basis (#125) ist kein Fehler, nur Hinweis — nur „Basis-Rollout starten“ verlangt eine Handlung.
        <div className={cn('text-[11px] mt-0.5', istHinweis(r.error_message) ? 'text-ink-2' : 'text-coral')} title={r.error_message ?? undefined}>{grund}</div>
      )}
    </div>
  )
}

/** Rückfall für ältere Antworten ohne Zeitplan. */
function lockCountdown(lockedUntil: string | null): string {
  if (!lockedUntil) return '—'
  const ms = new Date(lockedUntil).getTime() - Date.now()
  if (ms <= 0) return 'frei'
  const days = Math.ceil(ms / 86400000)
  return `noch ${days} Tag${days === 1 ? '' : 'e'} (bis ${new Date(lockedUntil).toLocaleDateString('de-DE')})`
}

function SchreibzielZelle({ r }: { r: RolloutStatus }) {
  return (
    <div className="leading-tight">
      <div className="text-xs font-medium text-ink">{portalText(r.portal)}{r.account ? <span className="text-muted"> · {r.account}</span> : null}</div>
      {r.schreibweg !== undefined && (
        <div className="text-[11px] text-ink-2"><span aria-hidden className="text-muted">→ </span>{schreibzielText(r.portal, r.schreibweg)}</div>
      )}
    </div>
  )
}

function IstZielZelle({ r }: { r: RolloutStatus }) {
  const ist = r.ist_preis !== undefined ? r.ist_preis : r.last_applied_price
  return (
    <div className="font-mono text-xs whitespace-nowrap" title={istQuelleText(r.ist_quelle) ?? undefined}>
      <span className="text-ink-2">{ist === null ? '—' : fmtEUR(ist)}</span>
      <span aria-hidden className="text-muted"> → </span>
      <span className="font-semibold text-ink">{fmtEUR(r.target_price)}</span>
      {(r.konto_aufschlag_ct ?? 0) > 0 && <div className="font-sans text-[10px] text-muted">inkl. +{r.konto_aufschlag_ct} ct {r.konto_aufschlag_konto ? kontoKurz(r.konto_aufschlag_konto) : ''}</div>}
      {(r.konto_aufschlag_soll_ct ?? 0) > (r.konto_aufschlag_ct ?? 0) && <div className="font-sans text-[10px] text-muted">Aufschlag gekappt am Höchstpreis</div>}
      {r.ist_quelle === 'erbt_basis' && <div className="font-sans text-[10px] text-muted">Nur DR geändert, eBay-Übernahme nicht bestätigt</div>}
    </div>
  )
}

function NaechsterZelle({ r, praefix }: { r: RolloutStatus; praefix?: string }) {
  if (r.zeitplan) return <NaechsterSchritt zeitplan={r.zeitplan} praefix={praefix} />
  return <span className="text-xs">{praefix ? `${praefix} ` : ''}{lockCountdown(r.locked_until)}</span>
}

/** Paarzeile: beide Seiten getrennt untereinander (eigener Termin, eigener Grund). */
function Seiten({ basis, ebay, render }: { basis: RolloutStatus; ebay: RolloutStatus; render: (r: RolloutStatus) => ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      {[{ r: basis, n: 'Basis' }, { r: ebay, n: 'eBay' }].map(({ r, n }) => (
        <div key={r.id} className="flex items-start gap-1.5">
          <span className="mt-px w-8 shrink-0 text-[10px] uppercase tracking-wider text-muted">{n}</span>
          <div className="min-w-0">{render(r)}</div>
        </div>
      ))}
    </div>
  )
}

function suchtext(z: RolloutZeile): string {
  const r = z.art === 'einzeln' ? z.r : z.basis
  return kennungSuchtext(r.kennung, r.sku, r.name)
}

const WIRKT_NICHT = new Set(['keine_regel', 'regel_aus', 'gesperrt'])

/** Zahlen für die Rückfrage „Alle fortsetzen“ — Fortsetzen betrifft nur pausierte Rollouts. */
function fortsetzenZahlen(rows: readonly RolloutStatus[]) {
  const pausiert = rows.filter((r) => r.status === 'paused')
  const exkl = pausiert.filter((r) => r.versand === 'exkl')
  const rest = pausiert.filter((r) => r.versand !== 'exkl')
  const wirktNicht = rest.filter((r) => r.wirkung_konfiguriert !== undefined && WIRKT_NICHT.has(r.wirkung_konfiguriert))
  const live = rest.filter((r) => r.wirkung_konfiguriert === 'live')
  return { pausiert, exkl, wirktNicht, live }
}

const kurz = (r: RolloutStatus) => `${r.kennung?.stern ?? r.kennung?.sku ?? r.sku ?? r.stock_item_id} · ${portalText(r.portal)}`

function Zahl({ id, wert, children }: { id: string; wert: number; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line-2 py-1.5 last:border-0">
      <span className="text-sm text-ink-2">{children}</span>
      <span data-testid={id} className="font-mono text-base font-bold text-ink">{wert}</span>
    </div>
  )
}

export function RolloutsTab() {
  const neuLaden = useRolloutStatusNeuLaden()
  const { data, isLoading } = useRolloutStatus()
  const rollouts = useMemo(() => data?.rollouts ?? [], [data])
  const betrieb = data?.betrieb ?? null
  const [suche, setSuche] = useState('')
  const [dialog, setDialog] = useState<'notaus' | 'fortsetzen' | null>(null)
  const [dialogFehler, setDialogFehler] = useState<string | null>(null)

  const zeilen = useMemo(() => {
    const alle = gruppiereRollouts(rollouts)
    const q = suche.trim().toLowerCase()
    return q === '' ? alle : alle.filter((z) => suchtext(z).includes(q))
  }, [rollouts, suche])
  const zahlen = useMemo(() => fortsetzenZahlen(rollouts), [rollouts])
  // S4: Verlauf + Verkauf; im Paar liefern beide ids dieselbe Zeile, abgefragt wird die Basis.
  const verlauf = useRolloutVerlauf(useMemo(() => gruppiereRollouts(rollouts).map((z) => (z.art === 'einzeln' ? z.r.id : z.basis.id)), [rollouts]))
  const verlaufZeile = (id: number, kennung: RolloutStatus['kennung']) => (
    <tr data-testid="verlauf-zeile">
      <td colSpan={7} className="pb-2.5 pt-0">
        <div className="border-l-2 border-line-2 pl-3">
          <RolloutVerlauf verlauf={verlauf.data?.[String(id)]} laedt={verlauf.isLoading} fehler={verlauf.isError} kennung={kennung} />
        </div>
      </td>
    </tr>
  )
  const aktiv = rollouts.filter((r) => r.status === 'active').length

  const single = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'paused' | 'active' }) => setRolloutStatus(id, status),
    onSuccess: () => { void neuLaden(); toast.success('Rollout aktualisiert') },
    onError: (e) => toast.error(fehlerText(e)),
  })
  const pausieren = useMutation({
    mutationFn: pauseAll,
    onSuccess: (n) => { void neuLaden(); toast.success(`${n} Rollouts pausiert`) },
    onError: (e) => toast.error(fehlerText(e)),
  })
  const schliessen = () => { setDialog(null); setDialogFehler(null) }
  const notAus = useMutation({
    mutationFn: killAll,
    onSuccess: (n) => { schliessen(); toast.success(`Not-Aus ausgelöst – ${n} Rollouts pausiert`) },
    onError: (e) => setDialogFehler(fehlerText(e)),
    onSettled: () => { void neuLaden() },
  })
  const fortsetzen = useMutation({
    mutationFn: resumeAll,
    onSuccess: (r) => {
      schliessen()
      toast.success(`${r.resumed} fortgesetzt${r.skipped_exkl > 0 ? ` · ${r.skipped_exkl} mit Plus Versand ${r.skipped_exkl === 1 ? 'bleibt' : 'bleiben'} pausiert` : ''}`)
    },
    onError: (e) => setDialogFehler(fehlerText(e)),
    onSettled: () => { void neuLaden() },
  })

  return (
    <Panel
      title="Rollouts"
      subtitle="Je Rollout: wohin er schreibt, ob er wirkt und wann der nächste Schritt kommt. Basis und eBay laufen gekoppelt in einer Zeile."
      actions={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => pausieren.mutate()}>
            <Pause className="size-3.5" /> Alle pausieren
          </Button>
          <Button size="sm" variant="outline" onClick={() => setDialog('fortsetzen')}>
            <Play className="size-3.5" /> Alle fortsetzen
          </Button>
          <Button size="sm" variant="outline" className="text-coral hover:text-coral" onClick={() => setDialog('notaus')}>
            <OctagonX className="size-3.5" /> Not-Aus
          </Button>
        </div>
      }
    >
      <Input
        type="search"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        placeholder="Suchen: *367, DR-10015, Name oder Grundartikel"
        aria-label="Rollouts durchsuchen"
        className="max-w-sm mb-3"
      />
      {isLoading ? (
        <div className="py-6 text-center text-ink-2">Lade…</div>
      ) : rollouts.length === 0 ? (
        <div className="py-6 text-center text-ink-2">Keine Rollouts vorhanden. Gestartet wird im Grundartikel, Reiter „Preis-Rollout“.</div>
      ) : zeilen.length === 0 ? (
        <div className="py-6 text-center text-ink-2">Kein Rollout passt zu „{suche}“.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted text-left">
                <th className="pb-2 pr-3">Artikel</th>
                <th className="pb-2 pr-3">Portal → Schreibziel</th>
                <th className="pb-2 pr-3">Wirkung</th>
                <th className="pb-2 pr-3">Ist → Ziel</th>
                <th className="pb-2 pr-3">Nächster Schritt</th>
                <th className="pb-2 pr-3">Status</th>
                <th className="pb-2 w-20"></th>
              </tr>
            </thead>
            {zeilen.map((z) => {
                if (z.art === 'einzeln') {
                  const r = z.r
                  return (
                    <tbody key={r.id} className="border-t border-line-2 hover:bg-panel-2/40">
                    <tr className="align-top">
                      <td className="py-2 pr-3">
                        <ArtikelKennung kennung={r.kennung} sku={r.sku ?? String(r.stock_item_id)} name={r.name} />
                        <div className="mt-1"><VersandChip versand={r.versand} /></div>
                      </td>
                      <td className="py-2 pr-3"><SchreibzielZelle r={r} /></td>
                      <td className="py-2 pr-3"><WirkungChip wirkung={r.wirkung_konfiguriert} /></td>
                      <td className="py-2 pr-3"><IstZielZelle r={r} /></td>
                      <td className="py-2 pr-3"><NaechsterZelle r={r} /></td>
                      <td className="py-2 pr-3"><StatusZelle r={r} /></td>
                      <td className="py-2 text-right">
                        {r.status === 'active' ? (
                          <Button size="sm" variant="ghost" aria-label="Pausieren" onClick={() => single.mutate({ id: r.id, status: 'paused' })}>
                            <Pause className="size-3" />
                          </Button>
                        ) : r.status !== 'done' ? (
                          <Button size="sm" variant="ghost" aria-label="Fortsetzen" onClick={() => single.mutate({ id: r.id, status: 'active' })}>
                            <Play className="size-3" />
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                    {verlaufZeile(r.id, r.kennung)}
                    </tbody>
                  )
                }
                const { basis, ebay } = z
                const pz = paarZustand({
                  basis: { id: basis.id, status: basis.status, fehler: basis.error_message },
                  ebay: { id: ebay.id, status: ebay.status, fehler: ebay.error_message },
                })
                return (
                  <tbody key={`paar-${basis.id}`} className="border-t border-line-2 hover:bg-panel-2/40">
                  <tr className="align-top" data-testid="rollout-paar">
                    <td className="py-2 pr-3">
                      <ArtikelKennung kennung={basis.kennung} sku={basis.sku ?? String(basis.stock_item_id)} name={basis.name} />
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <VersandChip versand={ebay.versand ?? basis.versand} />
                        <span className="inline-flex items-center gap-1 text-[10px] text-muted" title="Basis und eBay: zwei gekoppelte Rollouts, ein Takt">
                          <span aria-hidden className="inline-flex h-2.5 items-stretch gap-[2px]">
                            <span className="w-[3px] rounded-sm bg-ink-2" />
                            <span className="w-[3px] rounded-sm bg-primary" />
                          </span>
                          gekoppelt
                        </span>
                      </div>
                    </td>
                    <td className="py-2 pr-3"><Seiten basis={basis} ebay={ebay} render={(r) => <SchreibzielZelle r={r} />} /></td>
                    <td className="py-2 pr-3"><Seiten basis={basis} ebay={ebay} render={(r) => <WirkungChip wirkung={r.wirkung_konfiguriert} />} /></td>
                    <td className="py-2 pr-3"><Seiten basis={basis} ebay={ebay} render={(r) => <IstZielZelle r={r} />} /></td>
                    <td className="py-2 pr-3"><Seiten basis={basis} ebay={ebay} render={(r) => <NaechsterZelle r={r} />} /></td>
                    <td className="py-2 pr-3">
                      {pz.status === 'gemischt' ? (
                        <div className="flex flex-col gap-1">
                          <StatusZelle r={basis} praefix="Basis" />
                          <StatusZelle r={ebay} praefix="eBay" />
                        </div>
                      ) : (
                        <div>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_STYLE[basis.status]}`}>{STATUS_TEXT(basis)}</span>
                          {pz.grund && <div className="text-[11px] text-coral mt-0.5">{pz.grund}</div>}
                        </div>
                      )}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {pz.pauseId !== null && (
                        <Button size="sm" variant="ghost" aria-label="Basis + eBay pausieren" onClick={() => single.mutate({ id: pz.pauseId!, status: 'paused' })}>
                          <Pause className="size-3" />
                        </Button>
                      )}
                      {pz.fortsetzenId !== null && (
                        <Button size="sm" variant="ghost" aria-label="Basis + eBay fortsetzen" onClick={() => single.mutate({ id: pz.fortsetzenId!, status: 'active' })}>
                          <Play className="size-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                  {verlaufZeile(basis.id, basis.kennung)}
                  </tbody>
                )
              })}
          </table>
        </div>
      )}

      <BestaetigungsDialog
        offen={dialog === 'notaus'}
        titel="Not-Aus auslösen"
        beschreibung="Alle aktiven Rollouts werden sofort pausiert. Danach schreibt weder der Tageslauf noch ein Start Preise, bis der Not-Aus aufgehoben ist."
        aktion="Not-Aus auslösen"
        gefahr
        busy={notAus.isPending}
        fehler={dialogFehler}
        onAbbrechen={schliessen}
        onBestaetigen={() => notAus.mutate()}
      >
        <p className="text-sm text-ink">Betrifft <b className="font-mono">{aktiv}</b> aktive Rollouts.</p>
      </BestaetigungsDialog>

      <BestaetigungsDialog
        offen={dialog === 'fortsetzen'}
        titel="Alle pausierten Rollouts fortsetzen"
        beschreibung="Fortgesetzte Rollouts machen beim nächsten Tageslauf weiter. Rollouts mit Plus Versand bleiben pausiert (erst A2)."
        aktion="Fortsetzen"
        busy={fortsetzen.isPending}
        fehler={dialogFehler}
        gesperrt={betrieb?.kill_aktiv === true || zahlen.pausiert.length === 0}
        onAbbrechen={schliessen}
        onBestaetigen={() => fortsetzen.mutate()}
      >
        <div>
          <Zahl id="fortsetzen-pausiert" wert={zahlen.pausiert.length}>pausiert</Zahl>
          <Zahl id="fortsetzen-live" wert={zahlen.live.length}>davon LIVE eingestellt – schreiben echte Preise, sofern keine Sperre greift (z. B. EK fehlt)</Zahl>
          <Zahl id="fortsetzen-wirkt-nicht" wert={zahlen.wirktNicht.length}>davon wirken nicht (keine Regel, Regel aus, gesperrt)</Zahl>
          <Zahl id="fortsetzen-exkl" wert={zahlen.exkl.length}>davon Plus Versand – bleiben pausiert</Zahl>
        </div>
        {zahlen.wirktNicht.length > 0 && (
          <ul className="max-h-32 overflow-y-auto rounded-md bg-panel-2 px-3 py-2 text-xs text-ink-2">
            {zahlen.wirktNicht.slice(0, 10).map((r) => (
              <li key={r.id}><span className="font-mono font-semibold text-ink">{kurz(r)}</span> – {wirkungText(r.wirkung_konfiguriert)?.text}</li>
            ))}
            {zahlen.wirktNicht.length > 10 && <li>… und {zahlen.wirktNicht.length - 10} weitere</li>}
          </ul>
        )}
        {betrieb?.kill_aktiv && (
          <p className="rounded-md bg-warn-soft px-3 py-2 text-sm text-ink">Not-Aus aktiv: Fortsetzen wirkt erst, wenn der Not-Aus oben aufgehoben ist.</p>
        )}
      </BestaetigungsDialog>
    </Panel>
  )
}
